// Internal translation dictionaries — NO browser/Google auto-translation.
// Brand name "Blue Ocean Motion" stays unchanged in every language.
// "Motion Card" is treated as a brand-like feature name.

export const translations = {
  en: {
    brand: "Blue Ocean Motion",
    common: {
      min: "min",
      day: "day",
      days: "days",
      tryAgain: "Try again",
      addMovement: "Add Movement",
      today: "Today",
      yesterday: "Yesterday",
    },
    nav: {
      home: "Home",
      progress: "Progress",
      profile: "Profile",
      community: "Community",
      addAria: "Add Movement",
    },
    onboarding: {
      stage: "Stage 1 · Core MVP",
      tagline: "Move your body. Build your energy. Create momentum.",
      intro: "A simple app to track your movement, build consistency and feel progress every day.",
      nameLabel: "Your name",
      nameOptional: "(optional)",
      namePlaceholder: "How should we call you?",
      goalQuestion: "What is your main goal?",
      start: "Start My Motion",
      chooseGoalError: "Choose your main goal to begin",
      createError: "Could not create your profile. Please try again.",
    },
    dashboard: {
      greeting: "Hello, {name}",
      greetingFallback: "Hello, Mover",
      ready: "Ready to move",
      inMotion: "In motion today",
      weeklyGoal: "Weekly goal",
      minToday: "min today",
      pctOfGoal: "{pct}% of this week's goal",
      todaysCurrent: "Today's current",
      activeThisWeek: "Active this week",
      currentStreak: "Current streak",
      error: "The ocean is quiet — we couldn't reach the server.",
    },
    addActivity: {
      title: "Add Movement",
      subtitle: "Every minute counts toward your momentum",
      backAria: "Go back",
      typeSection: "Activity type",
      durationSection: "Duration (minutes)",
      intensitySection: "Intensity",
      moodBeforeSection: "Mood before",
      moodAfterSection: "Mood after",
      noteSection: "Note (optional)",
      custom: "Custom",
      customPlaceholder: "Enter minutes (e.g. 25)",
      notePlaceholder: "How did it feel?",
      shareSection: "Community",
      shareLabel: "Share to Community",
      shareHintPrivate: "Your profile is private — sharing is off by default.",
      shareHintPublic: "Your movement will appear in the community feed.",
      save: "Save My Motion",
      chooseTypeError: "Choose an activity type",
      durationError: "Enter a duration between 1 and 720 minutes",
      saveError: "Could not save your movement. Please try again.",
    },
    motionCard: {
      you: "You",
      summary: "You completed {duration} of movement today.",
      goalLabel: "Goal",
      statusLabel: "Status",
      intensityLabel: "Intensity",
      done: "Back to Dashboard",
      viewProgress: "View My Progress",
      statusPool: [
        "One step forward.",
        "Momentum created.",
        "Consistency in action.",
        "Energy unlocked.",
        "The current keeps moving.",
      ],
      sentencePool: [
        "Every minute of movement is a message to your future self.",
        "You didn't wait for motivation. You created it.",
        "Progress is quiet. It looks exactly like today.",
        "Strong currents are built one wave at a time.",
        "Your body keeps the score — today it scored a win.",
      ],
    },
    progress: {
      kicker: "This week",
      title: "Your Progress",
      activitiesThisWeek: "Activities this week",
      totalMinutes: "Total minutes",
      bestDay: "Best day",
      currentStreak: "Current streak",
      recent: "Recent activities",
      emptyLine1: "Your ocean is calm. Add your first movement",
      emptyLine2: "and start building momentum.",
      deleted: "Activity removed",
      deleteError: "Could not remove the activity",
      deleteAria: "Delete activity",
      editAria: "Edit activity",
      deleteConfirmTitle: "Remove this activity?",
      deleteConfirmDesc: "Your weekly stats, streak and challenges will update. A shared community post will also be removed.",
      deleteConfirmCancel: "Cancel",
      deleteConfirmDelete: "Remove",
      error: "We couldn't load your progress right now.",
    },
    editActivity: {
      title: "Edit Movement",
      subtitle: "Adjust the details of this movement",
      backAria: "Back to progress",
      save: "Save Changes",
      cancel: "Cancel",
      saved: "Activity updated",
      saveError: "Could not update the activity. Please try again.",
      loadError: "We couldn't load this activity right now.",
      notFound: "This activity no longer exists.",
      backToProgress: "Back to Progress",
    },
    recap: {
      kicker: "Weekly Recap",
      title: "Your week in motion",
      totalMinutes: "Active minutes",
      activities: "Activities",
      streak: "Streak",
      bestDay: "Best day",
      mostFrequent: "Most frequent",
      goalProgress: "Weekly goal",
      shareSummary: "Share summary",
      copySummary: "Copy summary",
      copied: "Weekly summary copied",
      copyError: "Could not copy the summary",
      empty: "A calm week so far. Your first movement will start this week's recap.",
      summaryTemplate: "Blue Ocean Motion — Weekly Recap\n{minutes} active minutes · {count} activities\nStreak: {streak}\nBest day: {bestDay}\nMost frequent: {type}\nWeekly goal: {pct}%\n\n{message}",
      pool: [
        "Every wave you rode this week made the current stronger.",
        "A week in motion is a promise kept to yourself.",
        "Consistency looks quiet up close — and powerful from a distance.",
        "This week's minutes are next week's momentum.",
        "You moved, you showed up, you kept the ocean alive.",
      ],
    },
    celebration: {
      kicker: "Challenge complete",
      title: "You did it!",
      resultLabel: "Result",
      viewChallenge: "View Challenge",
      viewProgress: "View Progress",
      backToCommunity: "Back to Community",
      sentencePool: [
        "You set the goal. You rode the wave. You finished it.",
        "Completed — not by luck, but by showing up.",
        "This is what momentum looks like when it wins.",
        "One challenge down. Your current is getting stronger.",
        "Steady effort just turned into a real achievement.",
      ],
    },
    nudge: {
      title: "Keep the current alive",
      message: "Your streak of {days} is still going. One small movement today keeps it flowing.",
    },
    deleteProfile: {
      button: "Delete profile and data",
      title: "Delete profile and data?",
      desc: "This permanently deletes your profile, all activities, community posts, kudos, follows and challenge progress from our servers. This cannot be undone.",
      confirm: "Delete permanently",
      cancel: "Cancel",
      success: "Your profile and data were deleted",
      error: "Could not delete your data. Please try again.",
    },
    profile: {
      kicker: "Your space",
      title: "Profile",
      legalTitle: "Legal & data",
      legalPrivacy: "Privacy Policy",
      legalTerms: "Terms of Use",
      legalDeleteInfo: "Data deletion info",
      name: "Name",
      namePlaceholder: "Your name",
      mainGoal: "Main goal",
      goalPlaceholder: "Choose a goal",
      preferredActivity: "Preferred activity",
      activityPlaceholder: "Choose an activity",
      level: "Level",
      levelPlaceholder: "Choose your level",
      weeklyGoalMinutes: "Weekly goal (minutes)",
      save: "Save Changes",
      saved: "Profile updated",
      weeklyGoalError: "Weekly goal must be between 10 and 5000 minutes",
      saveError: "Could not save your profile. Please try again.",
      privateTitle: "Private profile",
      privateDesc: "Stage 1 · Your data stays on your device & private account",
      privateDescOn: "Your activities remain private unless you choose to share them.",
      privateDescOff: "Public — you can share activities to the community.",
      visibilityUpdated: "Visibility updated",
      communitySection: "Community profile",
      displayName: "Display name",
      displayNamePlaceholder: "Name visible in Community",
      bioLabel: "Movement bio",
      bioPlaceholder: "Write a short movement motto or bio",
      language: "Language",
      futureTitle: "Coming in future waves",
      future1: "Trainers & gyms",
      future2: "Business profiles & networking",
      future3: "Cloud sync",
      future4: "Advanced challenges & premium ecosystem",
      resetLink: "Start fresh (reset local profile)",
      resetTitle: "Start fresh?",
      resetDesc: "This unlinks the profile from this browser. Your data stays on the server, but you'll go through onboarding again.",
      cancel: "Cancel",
      confirmReset: "Reset",
    },
    goals: {
      "More energy": "More energy",
      "Weight loss": "Weight loss",
      "Gym": "Gym",
      "Walking": "Walking",
      "Running": "Running",
      "Back to shape": "Back to shape",
      "Consistency": "Consistency",
      "Healthy lifestyle": "Healthy lifestyle",
    },
    types: {
      "Gym": "Gym",
      "Walk": "Walk",
      "Run": "Run",
      "Bike": "Bike",
      "Home workout": "Home workout",
      "Stretching": "Stretching",
      "Other": "Other",
    },
    intensities: {
      "Light": "Light",
      "Medium": "Medium",
      "Strong": "Strong",
    },
    moods: {
      "Tired": "Tired",
      "Low": "Low",
      "Okay": "Okay",
      "Good": "Good",
      "Energized": "Energized",
    },
    levels: {
      "Starting": "Starting",
      "Coming back": "Coming back",
      "Active": "Active",
    },
    messages: {
      before: [
        "Your movement is still ahead of you today. One small step is enough to create momentum.",
        "The hardest part is starting. Ten minutes counts.",
        "Your body is ready. Give it a signal.",
        "Small waves build strong currents. Start small today.",
        "You don't need a perfect workout. You need a first move.",
        "Energy comes after movement, not before it.",
        "Today is still open. One move changes its direction.",
      ],
      after: [
        "Great job. Your body received the signal: we are moving forward.",
        "Movement done. Momentum built. Well done today.",
        "You showed up today. That is what consistency looks like.",
        "One more day in motion. Your energy thanks you.",
        "Done is powerful. You kept the current moving.",
        "Steady beats intense. Today was steady.",
      ],
    },
    community: {
      kicker: "Move together",
      title: "Community",
      tabFeed: "Feed",
      tabChallenges: "Challenges",
      filterAll: "All",
      filterFollowing: "Following",
      follow: "Follow",
      following: "Following",
      demoBadge: "Demo",
      kudosAria: "Give kudos",
      emptyFeed: "The community is warming up. Share your first movement and start the wave.",
      emptyFollowing: "You're not following anyone yet. Follow someone from the feed to see their motion here.",
      loadError: "We couldn't load the community right now.",
      followedToast: "You're now following {name}",
      unfollowedToast: "You unfollowed {name}",
      timeNow: "just now",
      timeMin: "{n} min ago",
      timeHours: "{n} h ago",
      timeDays: "{n} d ago",
    },
    challengesContent: {
      progressLabel: "Your progress",
      participants: "{n} participants",
      join: "Join",
      leave: "Leave",
      joinedBadge: "Joined",
      joinedToast: "You joined the challenge",
      leftToast: "You left the challenge",
      "streak-7": {
        title: "7-Day Motion Streak",
        desc: "Move at least once a day, 7 days in a row.",
        unit: "days",
      },
      "weekly-150": {
        title: "150 Minutes This Week",
        desc: "Reach 150 active minutes before the week ends.",
        unit: "min",
      },
      "morning-walk": {
        title: "Morning Walk Challenge",
        desc: "Log 5 morning walks this week (before noon).",
        unit: "walks",
      },
    },
    premium: {
      title: "Premium ecosystem features coming later",
      items: "Advanced challenges · Trainers · Gyms · Business profiles · Networking · Cloud sync · Advanced progress",
    },
    report: {
      menuAria: "More options",
      reportPost: "Report post",
      reportUser: "Report user",
      titlePost: "Report this post",
      titleUser: "Report this user",
      desc: "Tell us what's wrong. Reports are confidential and reviewed by our team.",
      reason: {
        spam: "Spam or misleading",
        harassment: "Harassment or bullying",
        inappropriate: "Inappropriate content",
        other: "Something else",
      },
      noteLabel: "Additional details (optional)",
      notePlaceholder: "Anything else we should know?",
      submit: "Send report",
      cancel: "Cancel",
      success: "Thank you — your report was sent",
      duplicate: "You have already reported this",
      error: "Could not send the report. Please try again.",
    },
    block: {
      blockUser: "Block user",
      title: "Block {name}?",
      desc: "You won't see each other's posts anymore and any follows between you will be removed. You can unblock this user later from your Profile.",
      confirm: "Block",
      cancel: "Cancel",
      blockedToast: "{name} has been blocked",
      error: "Could not block this user. Please try again.",
      sectionTitle: "Blocked users",
      sectionDesc: "People you block can't see your posts and you won't see theirs.",
      empty: "You haven't blocked anyone.",
      unblock: "Unblock",
      unblockedToast: "User unblocked",
      unblockError: "Could not unblock this user. Please try again.",
      loadError: "We couldn't load your blocked users right now.",
    },
    ugcTerms: {
      title: "Community sharing terms",
      desc: "Before you share to the community for the first time, please accept our content rules.",
      rules: "By sharing, you agree to post only respectful, safe content: no harassment, hate speech, spam, or inappropriate material. You can report or block other users at any time, and content that violates the rules may be removed.",
      termsLink: "Terms of Use",
      privacyLink: "Privacy Policy",
      checkboxLabel: "I have read and accept the Terms of Use and the Privacy Policy",
      accept: "Accept and continue",
      cancel: "Not now",
      success: "Thanks! You can now share to the community.",
      error: "Could not save your acceptance. Please try again.",
      requiredError: "Please accept the community terms to share content.",
    },
  },

  pl: {
    brand: "Blue Ocean Motion",
    common: {
      min: "min",
      day: "dzień",
      days: "dni",
      tryAgain: "Spróbuj ponownie",
      addMovement: "Dodaj aktywność",
      today: "Dziś",
      yesterday: "Wczoraj",
    },
    nav: {
      home: "Start",
      progress: "Postęp",
      profile: "Profil",
      community: "Społeczność",
      addAria: "Dodaj aktywność",
    },
    onboarding: {
      stage: "Etap 1 · Core MVP",
      tagline: "Ruszaj ciałem. Buduj energię. Twórz momentum.",
      intro: "Prosta aplikacja do śledzenia ruchu, budowania konsekwencji i codziennego poczucia postępu.",
      nameLabel: "Twoje imię",
      nameOptional: "(opcjonalnie)",
      namePlaceholder: "Jak mamy się do Ciebie zwracać?",
      goalQuestion: "Jaki jest Twój główny cel?",
      start: "Rozpocznij mój ruch",
      chooseGoalError: "Wybierz swój główny cel, aby zacząć",
      createError: "Nie udało się utworzyć profilu. Spróbuj ponownie.",
    },
    dashboard: {
      greeting: "Cześć, {name}",
      greetingFallback: "Cześć!",
      ready: "Czas na ruch",
      inMotion: "Dziś w ruchu",
      weeklyGoal: "Cel tygodniowy",
      minToday: "min dzisiaj",
      pctOfGoal: "{pct}% celu na ten tydzień",
      todaysCurrent: "Prąd dnia",
      activeThisWeek: "Aktywność w tym tygodniu",
      currentStreak: "Aktualna seria",
      error: "Ocean milczy — nie udało się połączyć z serwerem.",
    },
    addActivity: {
      title: "Dodaj aktywność",
      subtitle: "Każda minuta buduje Twoje momentum",
      backAria: "Wróć",
      typeSection: "Rodzaj aktywności",
      durationSection: "Czas trwania (minuty)",
      intensitySection: "Intensywność",
      moodBeforeSection: "Samopoczucie przed",
      moodAfterSection: "Samopoczucie po",
      noteSection: "Notatka (opcjonalnie)",
      custom: "Własny",
      customPlaceholder: "Podaj minuty (np. 25)",
      notePlaceholder: "Jakie wrażenia?",
      shareSection: "Społeczność",
      shareLabel: "Udostępnij w społeczności",
      shareHintPrivate: "Twój profil jest prywatny — udostępnianie jest domyślnie wyłączone.",
      shareHintPublic: "Twoja aktywność pojawi się w aktualnościach społeczności.",
      save: "Zapisz mój ruch",
      chooseTypeError: "Wybierz rodzaj aktywności",
      durationError: "Podaj czas trwania od 1 do 720 minut",
      saveError: "Nie udało się zapisać aktywności. Spróbuj ponownie.",
    },
    motionCard: {
      you: "Ty",
      summary: "Dziś masz za sobą {duration} ruchu.",
      goalLabel: "Cel",
      statusLabel: "Status",
      intensityLabel: "Intensywność",
      done: "Wróć do pulpitu",
      viewProgress: "Zobacz mój postęp",
      statusPool: [
        "Krok do przodu.",
        "Momentum uruchomione.",
        "Konsekwencja w działaniu.",
        "Energia odblokowana.",
        "Prąd płynie dalej.",
      ],
      sentencePool: [
        "Każda minuta ruchu to wiadomość do Twojego przyszłego ja.",
        "Motywacja nie przyszła sama. Właśnie ją tworzysz.",
        "Postęp jest cichy. Wygląda dokładnie jak dzisiaj.",
        "Silne prądy powstają fala po fali.",
        "Twoje ciało wszystko zapisuje — dziś zapisało wygraną.",
      ],
    },
    progress: {
      kicker: "Ten tydzień",
      title: "Twój postęp",
      activitiesThisWeek: "Aktywności w tym tygodniu",
      totalMinutes: "Łączne minuty",
      bestDay: "Najlepszy dzień",
      currentStreak: "Aktualna seria",
      recent: "Ostatnie aktywności",
      emptyLine1: "Twój ocean jest spokojny. Dodaj pierwszą aktywność",
      emptyLine2: "i zacznij budować momentum.",
      deleted: "Aktywność usunięta",
      deleteError: "Nie udało się usunąć aktywności",
      deleteAria: "Usuń aktywność",
      editAria: "Edytuj aktywność",
      deleteConfirmTitle: "Usunąć tę aktywność?",
      deleteConfirmDesc: "Statystyki tygodnia, seria i wyzwania zostaną zaktualizowane. Powiązany wpis w społeczności również zostanie usunięty.",
      deleteConfirmCancel: "Anuluj",
      deleteConfirmDelete: "Usuń",
      error: "Nie udało się wczytać Twojego postępu.",
    },
    editActivity: {
      title: "Edytuj aktywność",
      subtitle: "Popraw szczegóły tej aktywności",
      backAria: "Wróć do postępu",
      save: "Zapisz zmiany",
      cancel: "Anuluj",
      saved: "Aktywność zaktualizowana",
      saveError: "Nie udało się zaktualizować aktywności. Spróbuj ponownie.",
      loadError: "Nie udało się teraz wczytać tej aktywności.",
      notFound: "Ta aktywność już nie istnieje.",
      backToProgress: "Wróć do postępu",
    },
    recap: {
      kicker: "Podsumowanie tygodnia",
      title: "Twój tydzień w ruchu",
      totalMinutes: "Aktywne minuty",
      activities: "Aktywności",
      streak: "Seria",
      bestDay: "Najlepszy dzień",
      mostFrequent: "Najczęstsza aktywność",
      goalProgress: "Cel tygodniowy",
      shareSummary: "Udostępnij podsumowanie",
      copySummary: "Kopiuj podsumowanie",
      copied: "Podsumowanie tygodnia skopiowane",
      copyError: "Nie udało się skopiować podsumowania",
      empty: "Na razie spokojny tydzień. Pierwsza aktywność rozpocznie podsumowanie tego tygodnia.",
      summaryTemplate: "Blue Ocean Motion — Podsumowanie tygodnia\n{minutes} aktywnych minut · {count} aktywności\nSeria: {streak}\nNajlepszy dzień: {bestDay}\nNajczęstsza aktywność: {type}\nCel tygodniowy: {pct}%\n\n{message}",
      pool: [
        "Każda fala z tego tygodnia wzmocniła Twój prąd.",
        "Tydzień w ruchu to obietnica dotrzymana samemu sobie.",
        "Konsekwencja z bliska wygląda cicho — a z daleka potężnie.",
        "Minuty z tego tygodnia to momentum na kolejny.",
        "Ruszasz się, jesteś tu — ocean płynie dalej.",
      ],
    },
    celebration: {
      kicker: "Wyzwanie ukończone",
      title: "Udało się!",
      resultLabel: "Wynik",
      viewChallenge: "Zobacz wyzwanie",
      viewProgress: "Zobacz postęp",
      backToCommunity: "Wróć do społeczności",
      sentencePool: [
        "Cel postawiony, fala złapana, wyzwanie ukończone.",
        "To nie przypadek — to Twoja regularność.",
        "Tak wygląda momentum, kiedy wygrywa.",
        "Jedno wyzwanie za Tobą. Twój prąd rośnie w siłę.",
        "Systematyczny wysiłek właśnie zmienił się w prawdziwe osiągnięcie.",
      ],
    },
    nudge: {
      title: "Podtrzymaj swój prąd",
      message: "Twoja seria ({days}) wciąż trwa. Jeden mały ruch dziś utrzyma ją w biegu.",
    },
    deleteProfile: {
      button: "Usuń profil i dane",
      title: "Usunąć profil i dane?",
      desc: "To trwale usunie z naszych serwerów Twój profil, wszystkie aktywności, wpisy w społeczności, kudos, obserwowania i postęp w wyzwaniach. Tej operacji nie można cofnąć.",
      confirm: "Usuń trwale",
      cancel: "Anuluj",
      success: "Twój profil i dane zostały usunięte",
      error: "Nie udało się usunąć danych. Spróbuj ponownie.",
    },
    profile: {
      kicker: "Twoja przestrzeń",
      title: "Profil",
      legalTitle: "Prawne i dane",
      legalPrivacy: "Polityka prywatności",
      legalTerms: "Regulamin",
      legalDeleteInfo: "Informacje o usuwaniu danych",
      name: "Imię",
      namePlaceholder: "Twoje imię",
      mainGoal: "Główny cel",
      goalPlaceholder: "Wybierz cel",
      preferredActivity: "Ulubiona aktywność",
      activityPlaceholder: "Wybierz aktywność",
      level: "Poziom",
      levelPlaceholder: "Wybierz poziom",
      weeklyGoalMinutes: "Cel tygodniowy (minuty)",
      save: "Zapisz zmiany",
      saved: "Profil zaktualizowany",
      weeklyGoalError: "Cel tygodniowy musi wynosić od 10 do 5000 minut",
      saveError: "Nie udało się zapisać profilu. Spróbuj ponownie.",
      privateTitle: "Profil prywatny",
      privateDesc: "Etap 1 · Twoje dane pozostają prywatne",
      privateDescOn: "Twoje aktywności pozostają prywatne, dopóki nie zdecydujesz się ich udostępnić.",
      privateDescOff: "Publiczny — możesz udostępniać aktywności społeczności.",
      visibilityUpdated: "Widoczność zaktualizowana",
      communitySection: "Profil społecznościowy",
      displayName: "Nazwa wyświetlana",
      displayNamePlaceholder: "Nazwa widoczna w społeczności",
      bioLabel: "Opis aktywności",
      bioPlaceholder: "Napisz krótkie motto lub opis swojej aktywności",
      language: "Język",
      futureTitle: "W kolejnych falach",
      future1: "Trenerzy i siłownie",
      future2: "Profile biznesowe i networking",
      future3: "Synchronizacja w chmurze",
      future4: "Zaawansowane wyzwania i ekosystem premium",
      resetLink: "Zacznij od nowa (zresetuj lokalny profil)",
      resetTitle: "Zacząć od nowa?",
      resetDesc: "To odłącza profil od tej przeglądarki. Twoje dane pozostaną na serwerze, ale przejdziesz onboarding ponownie.",
      cancel: "Anuluj",
      confirmReset: "Resetuj",
    },
    goals: {
      "More energy": "Więcej energii",
      "Weight loss": "Redukcja wagi",
      "Gym": "Siłownia",
      "Walking": "Spacery",
      "Running": "Bieganie",
      "Back to shape": "Powrót do formy",
      "Consistency": "Konsekwencja",
      "Healthy lifestyle": "Zdrowy styl życia",
    },
    types: {
      "Gym": "Siłownia",
      "Walk": "Spacer",
      "Run": "Bieg",
      "Bike": "Rower",
      "Home workout": "Trening w domu",
      "Stretching": "Rozciąganie",
      "Other": "Inne",
    },
    intensities: {
      "Light": "Lekka",
      "Medium": "Średnia",
      "Strong": "Mocna",
    },
    moods: {
      "Tired": "Zmęczenie",
      "Low": "Słabo",
      "Okay": "W porządku",
      "Good": "Dobrze",
      "Energized": "Pełnia energii",
    },
    levels: {
      "Starting": "Zaczynam",
      "Coming back": "Wracam do formy",
      "Active": "Aktywnie",
    },
    messages: {
      before: [
        "Twój ruch jest jeszcze przed Tobą. Wystarczy jeden mały krok, żeby uruchomić momentum.",
        "Najtrudniejszy jest początek. Dziesięć minut ma znaczenie.",
        "Twoje ciało jest gotowe. Daj mu sygnał.",
        "Małe fale budują silne prądy. Zacznij dziś od małego kroku.",
        "Nie potrzebujesz idealnego treningu. Potrzebujesz pierwszego ruchu.",
        "Energia przychodzi po ruchu, nie przed nim.",
        "Dzisiejszy dzień jest wciąż otwarty. Jeden ruch może zmienić jego kierunek.",
      ],
      after: [
        "Świetna robota. Twoje ciało dostało sygnał: idziemy do przodu.",
        "Ruch zaliczony. Momentum zbudowane. Dobra robota.",
        "Jesteś tu dziś. Tak właśnie wygląda konsekwencja.",
        "Kolejny dzień w ruchu. Twoja energia Ci dziękuje.",
        "Zrobione znaczy więcej niż idealne. Prąd płynie dalej.",
        "Regularność wygrywa z intensywnością. Dziś było regularnie.",
      ],
    },
    community: {
      kicker: "Razem w ruchu",
      title: "Społeczność",
      tabFeed: "Aktualności",
      tabChallenges: "Wyzwania",
      filterAll: "Wszyscy",
      filterFollowing: "Obserwowani",
      follow: "Obserwuj",
      following: "Obserwujesz",
      demoBadge: "Demo",
      kudosAria: "Dodaj kudos",
      emptyFeed: "Społeczność się rozgrzewa. Udostępnij swój pierwszy ruch i zacznij falę.",
      emptyFollowing: "Nie obserwujesz jeszcze nikogo. Zacznij obserwować osoby z aktualności, aby widzieć tu ich ruch.",
      loadError: "Nie udało się wczytać społeczności.",
      followedToast: "Obserwujesz teraz {name}",
      unfollowedToast: "Przestajesz obserwować {name}",
      timeNow: "przed chwilą",
      timeMin: "{n} min temu",
      timeHours: "{n} godz. temu",
      timeDays: "{n} dni temu",
    },
    challengesContent: {
      progressLabel: "Twój postęp",
      participants: "{n} uczestników",
      join: "Dołącz",
      leave: "Opuść",
      joinedBadge: "Dołączono",
      joinedToast: "Dołączasz do wyzwania",
      leftToast: "Opuszczasz wyzwanie",
      "streak-7": {
        title: "7-dniowa seria ruchu",
        desc: "Ruszaj się przynajmniej raz dziennie przez 7 dni z rzędu.",
        unit: "dni",
      },
      "weekly-150": {
        title: "150 minut w tym tygodniu",
        desc: "Osiągnij 150 aktywnych minut przed końcem tygodnia.",
        unit: "min",
      },
      "morning-walk": {
        title: "Wyzwanie porannych spacerów",
        desc: "Zapisz 5 porannych spacerów w tym tygodniu (przed południem).",
        unit: "spacery",
      },
    },
    premium: {
      title: "Funkcje premium ekosystemu pojawią się później",
      items: "Zaawansowane wyzwania · Trenerzy · Siłownie · Profile biznesowe · Networking · Synchronizacja w chmurze · Zaawansowany postęp",
    },
    report: {
      menuAria: "Więcej opcji",
      reportPost: "Zgłoś wpis",
      reportUser: "Zgłoś użytkownika",
      titlePost: "Zgłoś ten wpis",
      titleUser: "Zgłoś tego użytkownika",
      desc: "Powiedz nam, co jest nie tak. Zgłoszenia są poufne i weryfikowane przez nasz zespół.",
      reason: {
        spam: "Spam lub wprowadzanie w błąd",
        harassment: "Nękanie lub dręczenie",
        inappropriate: "Nieodpowiednie treści",
        other: "Coś innego",
      },
      noteLabel: "Dodatkowe informacje (opcjonalnie)",
      notePlaceholder: "Czy jest coś jeszcze, co powinniśmy wiedzieć?",
      submit: "Wyślij zgłoszenie",
      cancel: "Anuluj",
      success: "Dziękujemy — zgłoszenie zostało wysłane",
      duplicate: "To zgłoszenie zostało już wysłane",
      error: "Nie udało się wysłać zgłoszenia. Spróbuj ponownie.",
    },
    block: {
      blockUser: "Zablokuj użytkownika",
      title: "Zablokować {name}?",
      desc: "Nie będziecie już widzieć swoich wpisów, a wzajemne obserwowania zostaną usunięte. Możesz później odblokować tę osobę w swoim Profilu.",
      confirm: "Zablokuj",
      cancel: "Anuluj",
      blockedToast: "{name} — użytkownik zablokowany",
      error: "Nie udało się zablokować użytkownika. Spróbuj ponownie.",
      sectionTitle: "Zablokowani użytkownicy",
      sectionDesc: "Zablokowane osoby nie widzą Twoich wpisów, a Ty nie widzisz ich.",
      empty: "Nie masz zablokowanych użytkowników.",
      unblock: "Odblokuj",
      unblockedToast: "Użytkownik odblokowany",
      unblockError: "Nie udało się odblokować użytkownika. Spróbuj ponownie.",
      loadError: "Nie udało się wczytać listy zablokowanych użytkowników.",
    },
    ugcTerms: {
      title: "Zasady udostępniania w społeczności",
      desc: "Zanim po raz pierwszy udostępnisz coś społeczności, zaakceptuj nasze zasady dotyczące treści.",
      rules: "Udostępniając, zobowiązujesz się publikować wyłącznie treści bezpieczne i pełne szacunku: bez nękania, mowy nienawiści, spamu ani nieodpowiednich materiałów. W każdej chwili możesz zgłosić lub zablokować innych użytkowników, a treści łamiące zasady mogą zostać usunięte.",
      termsLink: "Regulamin",
      privacyLink: "Polityka prywatności",
      checkboxLabel: "Zapoznałem/am się z Regulaminem i Polityką prywatności i je akceptuję",
      accept: "Akceptuję i kontynuuję",
      cancel: "Nie teraz",
      success: "Dziękujemy! Możesz teraz udostępniać w społeczności.",
      error: "Nie udało się zapisać akceptacji. Spróbuj ponownie.",
      requiredError: "Aby udostępniać treści, zaakceptuj zasady społeczności.",
    },
  },
};

/**
 * Human-friendly "X minutes" phrase with correct pluralization.
 * PL rules: 1 -> minutę (fits "masz za sobą 1 minutę"), 2-4 (but not 12-14) -> minuty, else -> minut.
 */
export const formatMinutesLong = (n, lang) => {
  if (lang === "pl") {
    if (n === 1) return "1 minutę";
    const lastTwo = n % 100;
    const last = n % 10;
    if (last >= 2 && last <= 4 && !(lastTwo >= 12 && lastTwo <= 14)) return `${n} minuty`;
    return `${n} minut`;
  }
  return n === 1 ? "1 minute" : `${n} minutes`;
};
